import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { useToast } from '../../hooks/use-toast';
import { Calendar, Clock, Users, MessageSquare, ArrowLeft } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select';

import axios from 'axios';

const MeetingRequest = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    meeting_title: '',
    meeting_date: '',
    meeting_time: '',
    duration: '60',
    meeting_type: 'virtual',
    location: '',
    agenda: '',
    attendees: '',
    priority: 'normal',
    notes: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.meeting_title || !formData.meeting_date || !formData.meeting_time || !formData.agenda) {
      toast({
        title: 'Missing fields',
        description: 'Please fill in all required fields',
        variant: 'destructive'
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Convert duration to integer
      const requestData = {
        ...formData,
        duration: parseInt(formData.duration)
      };

      await axios.post(
        `${backendUrl}/api/executive/meeting-requests`,
        requestData
      );
      
      toast({
        title: 'Meeting Request Submitted',
        description: 'Your meeting request has been submitted successfully and is pending approval.'
      });
      
      // Reset form
      setFormData({
        meeting_title: '',
        meeting_date: '',
        meeting_time: '',
        duration: '60',
        meeting_type: 'virtual',
        location: '',
        agenda: '',
        attendees: '',
        priority: 'normal',
        notes: ''
      });
    } catch (error) {
      toast({
        title: 'Submission Failed',
        description: error.response?.data?.detail || 'Failed to submit meeting request. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-[#2c3e7d] text-white py-16">
        <div className="container mx-auto px-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/executive')}
            className="text-white hover:text-white hover:bg-white/10 mb-4"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Executive Members
          </Button>
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold uppercase tracking-wide">
              MEETING REQUEST
            </h1>
            <div className="w-24 h-1 bg-white mx-auto mt-4"></div>
          </div>
        </div>
      </div>
      
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl">Request Executive Meeting</CardTitle>
              <p className="text-sm text-gray-600 mt-2">
                Submit a request for an executive committee meeting. All fields marked with * are required.
              </p>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Meeting Details */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Meeting Details</h3>
                  
                  <div>
                    <Label htmlFor="meeting_title">
                      Meeting Title *
                    </Label>
                    <Input
                      id="meeting_title"
                      name="meeting_title"
                      value={formData.meeting_title}
                      onChange={handleChange}
                      placeholder="e.g., Q4 Budget Review"
                      className="mt-2"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="meeting_date">Date *</Label>
                      <Input
                        id="meeting_date"
                        name="meeting_date"
                        type="date"
                        value={formData.meeting_date}
                        onChange={handleChange}
                        className="mt-2"
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="meeting_time">Time *</Label>
                      <Input
                        id="meeting_time"
                        name="meeting_time"
                        type="time"
                        value={formData.meeting_time}
                        onChange={handleChange}
                        className="mt-2"
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="duration">Duration (minutes)</Label>
                      <Select
                        value={formData.duration}
                        onValueChange={(value) => setFormData(prev => ({ ...prev, duration: value }))}
                      >
                        <SelectTrigger className="mt-2">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="30">30 minutes</SelectItem>
                          <SelectItem value="60">1 hour</SelectItem>
                          <SelectItem value="90">1.5 hours</SelectItem>
                          <SelectItem value="120">2 hours</SelectItem>
                          <SelectItem value="180">3 hours</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="meeting_type">Meeting Type</Label>
                      <Select
                        value={formData.meeting_type}
                        onValueChange={(value) => setFormData(prev => ({ ...prev, meeting_type: value }))}
                      >
                        <SelectTrigger className="mt-2">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="virtual">Virtual (Zoom/Teams)</SelectItem>
                          <SelectItem value="in-person">In-Person</SelectItem>
                          <SelectItem value="hybrid">Hybrid</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="priority">Priority</Label>
                      <Select
                        value={formData.priority}
                        onValueChange={(value) => setFormData(prev => ({ ...prev, priority: value }))}
                      >
                        <SelectTrigger className="mt-2">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low</SelectItem>
                          <SelectItem value="normal">Normal</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                          <SelectItem value="urgent">Urgent</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {formData.meeting_type === 'in-person' || formData.meeting_type === 'hybrid' ? (
                    <div>
                      <Label htmlFor="location">Location</Label>
                      <Input
                        id="location"
                        name="location"
                        value={formData.location}
                        onChange={handleChange}
                        placeholder="e.g., Boardroom, 3rd Floor, Main Office"
                        className="mt-2"
                      />
                    </div>
                  ) : null}
                </div>

                {/* Agenda & Attendees */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Agenda & Attendees</h3>
                  
                  <div>
                    <Label htmlFor="agenda">
                      Meeting Agenda *
                    </Label>
                    <Textarea
                      id="agenda"
                      name="agenda"
                      value={formData.agenda}
                      onChange={handleChange}
                      placeholder="List the main topics to be discussed..."
                      rows={6}
                      className="mt-2"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="attendees">
                      Requested Attendees
                    </Label>
                    <Textarea
                      id="attendees"
                      name="attendees"
                      value={formData.attendees}
                      onChange={handleChange}
                      placeholder="List executive members who should attend (optional)"
                      rows={3}
                      className="mt-2"
                    />
                  </div>

                  <div>
                    <Label htmlFor="notes">
                      Additional Notes
                    </Label>
                    <Textarea
                      id="notes"
                      name="notes"
                      value={formData.notes}
                      onChange={handleChange}
                      placeholder="Any additional information or requirements..."
                      rows={4}
                      className="mt-2"
                    />
                  </div>
                </div>

                {/* Requester Info (Auto-filled) */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Requester Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-600">Name:</span>
                      <span className="ml-2 font-medium">{user?.full_name}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Email:</span>
                      <span className="ml-2 font-medium">{user?.email}</span>
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="flex justify-end gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/executive')}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-[#2c3e7d] hover:bg-[#1e2a5a]"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Submitting...
                      </>
                    ) : (
                      <>
                        <MessageSquare size={18} className="mr-2" />
                        Submit Request
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default MeetingRequest;